import LayoutUtil from '@frontend/utils/LayoutUtil';
import { FilterPanelEvent } from '@components/047-FilterPanel/constants';
import {
	IFilterContainerDataOptions,
	IFilterPanelEmitDetails
} from '@frontend/types/modules/content-filter/IContentFilter';

export default class FilterContainer {
	private readonly element: HTMLElement;

	public static readonly domSelectors = {
		resultContainer: '[data-filter-results]',
		filterableItem: '[data-filterable-item]',
		paginationWrapper: '.c-filter-container__pagination',
		paginationButton: '.c-filter-container__pagination-button',
		paginationText: '.c-filter-container__pagination-text'
	};

	public static readonly stateClasses = {
		hasFullyPaginated: 'has-fully-paginated',
		canPaginate: 'can-paginate'
	};

	public static readonly attributes = {
		filterState: 'data-filter-state',
		paginationState: 'data-pagination-state'
	};

	private readonly panel: HTMLElement | null;

	protected options: IFilterContainerOptions = {};

	private filterableElements: HTMLElement[] = [];

	public filteredElements: HTMLElement[] = [];

	private currentPaginationCount = 0;

	protected usePagination: boolean;

	protected paginationGroupSize: number;

	public initialized: boolean = false;

	public dataFromPanel?: IFilterPanelEmitDetails;

	public constructor(element: HTMLElement, options: IFilterContainerOptions = {}) {
		this.element = element;
		this.panel = options.panel ? options.panel : null;
		this.options = options;

		const dataOptions = JSON.parse(this.element.getAttribute('data-options') as string) as IFilterContainerDataOptions;

		this.usePagination = dataOptions.usePagination;
		this.paginationGroupSize = dataOptions.paginationGroupSize;

		this.init();
	}

	private init(): void {
		this.createSnapshotOfFilterableDomElements();
		this.initPagination();
		this.initPanelListener();

		const resultContainer = this.element.querySelector('[data-filter-results]') as HTMLElement;
		resultContainer.dispatchEvent(new CustomEvent('ready'));
	}

	private initPanelListener(): void {
		if (this.panel) {
			this.panel.addEventListener(FilterPanelEvent.FILTER_CHANGED, (event: CustomEventInit): void => {
				this.dataFromPanel = event.detail as IFilterPanelEmitDetails;
				this.filterDomElementsAndPrepend(this.dataFromPanel.filteredElementIds);
				this.element.setAttribute(FilterContainer.attributes.filterState, this.dataFromPanel.state);
				this.updatePagination(true);
			});
		}
	}

	private initPagination(): void {
		if (this.usePagination && this.filterableElements.length > this.paginationGroupSize) {
			const button = this.element.querySelector(FilterContainer.domSelectors.paginationButton);

			if (button) {
				button.addEventListener('click', (): void => this.updatePagination());
				this.updatePagination();
			}
		} else {
			const paginationWrapper = this.element.querySelector(FilterContainer.domSelectors.paginationWrapper);

			if (paginationWrapper) {
				paginationWrapper.remove();
			}
		}
	}

	private emptyContainer(): void {
		const filterableElements: HTMLElement[] = Array.from(
			this.element.querySelectorAll(FilterContainer.domSelectors.filterableItem)
		);

		filterableElements.forEach((element): void => element.remove());
	}

	private filterDomElementsAndPrepend(filteredElementIds: string[]): void {
		this.filteredElements = filteredElementIds.reduce((acc, id): HTMLElement[] => {
			const element = this.filterableElements.find((item): boolean => item.dataset.filterableItem === id);
			if (element) acc.push(element);
			return acc;
		}, [] as HTMLElement[]);

		const hasElementsToShow = this.filteredElements.length > 0;

		if (this.options.onBeforeEmptyContainer) {
			this.options.onBeforeEmptyContainer(this);
		}

		this.emptyContainer();

		if (hasElementsToShow) {
			const fragment = new DocumentFragment();

			this.filteredElements.forEach((element): void => {
				fragment.append(element);
			});

			this.getResultContainer().prepend(fragment);
		}

		if (this.options.onAfterRenderResult) {
			this.options.onAfterRenderResult(this);
		}

		if (this.initialized) {
			this.alignWithTopOfViewPort();
		} else {
			this.initialized = true;
		}
	}

	private updatePagination(reset?: boolean): void {
		if (!this.hasPagination()) return;

		const i18n: Record<string, string> = (window as any).HARIBO.i18n as Record<string, string>;
		const textElement: HTMLElement | null = this.element.querySelector(FilterContainer.domSelectors.paginationText);
		const items: HTMLElement[] = Array.from(this.element.querySelectorAll(FilterContainer.domSelectors.filterableItem));
		const itemsCount: number = items.length;

		if (reset) {
			this.currentPaginationCount = 0;
			this.element.removeAttribute(FilterContainer.attributes.paginationState);
		}

		this.currentPaginationCount += this.paginationGroupSize;

		items.forEach((item, index): void => {
			if (index >= this.currentPaginationCount) {
				item.classList.add('is-hidden');
			} else {
				item.classList.remove('is-hidden');
			}
		});

		if (this.currentPaginationCount >= itemsCount) {
			this.element.setAttribute(FilterContainer.attributes.paginationState, 'has-fully-paginated');
		} else {
			this.element.setAttribute(FilterContainer.attributes.paginationState, 'can-paginate');
		}

		if (textElement) {
			textElement.innerHTML = i18n.partFromTotalResults
				.replace('{part}', this.currentPaginationCount.toString())
				.replace('{total}', itemsCount.toString());
		}

		if (this.options.onAfterPaginate) {
			this.options.onAfterPaginate(this);
		}
	}

	private alignWithTopOfViewPort(): void {
		const resultBounds = this.getResultContainer().getBoundingClientRect();

		// Scroll into viewport if outside
		if (resultBounds.top < 0 || resultBounds.top >= window.innerHeight) {
			LayoutUtil.disableNavigationAndExecuteCallback((): void => {
				this.getResultContainer().scrollIntoView();
			});
		}
	}

	private createSnapshotOfFilterableDomElements(): void {
		this.filterableElements = Array.from(this.element.querySelectorAll(FilterContainer.domSelectors.filterableItem));
	}

	private getResultContainer(): HTMLElement {
		return this.element.querySelector(FilterContainer.domSelectors.resultContainer) as HTMLElement;
	}

	private hasPagination(): boolean {
		return !!this.element.querySelector(FilterContainer.domSelectors.paginationButton);
	}
}

interface IFilterContainerOptions {
	panel?: HTMLElement | null;
	onBeforeEmptyContainer?: (instance: FilterContainer) => void;
	onAfterRenderResult?: (instance: FilterContainer) => void;
	onAfterPaginate?: (instance: FilterContainer) => void;
}
